import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, CheckCircle2, ExternalLink, FlaskConical, Radio } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import api, { formatApiError } from "../lib/api";

const MODES = [
  { id: "sandbox", label: "Sandbox (test)", Icon: FlaskConical, color: "text-amber-600", hint: "Soldi finti: contributi di gruppo, gift card, ordini e rimborsi si provano con un account compratore di prova." },
  { id: "live", label: "Live (incassi reali)", Icon: Radio, color: "text-emerald-600", hint: "Pagamenti veri sul conto PayPal Business del club." },
];

function CredsBox({ m, creds, onChange, secret, setSecret, onTest, busy, active }) {
  return (
    <div className={`rounded-xl p-4 space-y-3 ring-1 ${active ? "ring-[color:var(--paprika)] bg-white" : "ring-zinc-200 bg-zinc-50"}`} data-testid={`paypal-creds-${m.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold flex items-center gap-2"><m.Icon className={`h-4 w-4 ${m.color}`} /> {m.label}{active && <span className="text-[10px] uppercase tracking-widest rounded-full bg-[color:var(--paprika)] text-white px-2 py-0.5" data-testid={`paypal-active-${m.id}`}>attiva</span>}</div>
        {creds.has_secret && <span className="text-emerald-600 text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> secret salvato</span>}
      </div>
      <div>
        <Label className="text-xs">Client ID</Label>
        <Input value={creds.client_id || ""} onChange={(e) => onChange({ ...creds, client_id: e.target.value })} placeholder="AXy…" data-testid={`paypal-client-id-${m.id}`} className="mt-1 font-mono text-xs" />
      </div>
      <div>
        <Label className="text-xs">Client Secret</Label>
        <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={creds.has_secret ? "•••••••• (lascia vuoto per non cambiarlo)" : "EH…"} data-testid={`paypal-client-secret-${m.id}`} className="mt-1 font-mono text-xs" />
      </div>
      <Button size="sm" variant="outline" onClick={() => onTest(m.id)} disabled={busy || !creds.has_secret} data-testid={`paypal-test-${m.id}`}>Verifica connessione {m.id}</Button>
    </div>
  );
}

// Impostazioni PayPal del club: credenziali separate Sandbox / Live, modalità attiva.
export default function PayPalSettings() {
  const [cfg, setCfg] = useState(null);
  const [secrets, setSecrets] = useState({ live: "", sandbox: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/paypal/config").then(({ data }) => setCfg(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { enabled: cfg.enabled, mode: cfg.mode };
      for (const m of ["live", "sandbox"]) body[m] = { client_id: cfg.creds[m].client_id || "", client_secret: secrets[m] || null };
      const { data } = await api.put("/paypal/config", body);
      setCfg(data); setSecrets({ live: "", sandbox: "" }); toast.success(`Impostazioni PayPal salvate · modalità ${data.mode === "live" ? "LIVE" : "SANDBOX"}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const test = async (mode) => {
    setBusy(true);
    try { await api.post(`/paypal/config/test?mode=${mode}`); toast.success(`Connessione PayPal ${mode} OK: credenziali valide`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!cfg) return <div className="text-sm text-zinc-500">Caricamento…</div>;
  const active = MODES.find((m) => m.id === cfg.mode) || MODES[0];
  return (
    <div className="space-y-6 animate-fade-up max-w-3xl" data-testid="paypal-settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><Wallet className="h-7 w-7 text-sky-500" /> PayPal</h1>
        <p className="text-sm text-zinc-500 mt-1">Incassi di ordini, gift card e regali di gruppo. Le credenziali Sandbox e Live restano entrambe salvate: scegli quale usare con l'interruttore di modalità.</p>
      </div>

      <div className="card-elev p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div><div className="font-semibold">Pagamenti PayPal attivi</div><div className="text-xs text-zinc-500">Mostra i pulsanti PayPal nel portale cliente, gift card e regali di gruppo</div></div>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="paypal-enabled" />
        </div>
        <div>
          <Label className="text-xs">Modalità attiva</Label>
          <div className="mt-1 grid sm:grid-cols-2 gap-2" data-testid="paypal-mode">
            {MODES.map((m) => (
              <button key={m.id} type="button" onClick={() => setCfg({ ...cfg, mode: m.id })} className={`text-left rounded-xl p-3 ring-1 transition ${cfg.mode === m.id ? "ring-2 ring-[color:var(--paprika)] bg-white" : "ring-zinc-200 bg-zinc-50 hover:bg-white"}`} data-testid={`paypal-mode-${m.id}`}>
                <div className="font-semibold text-sm flex items-center gap-2"><m.Icon className={`h-4 w-4 ${m.color}`} /> {m.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{m.hint}</div>
              </button>
            ))}
          </div>
          {cfg.mode === "sandbox" && <p className="text-xs text-amber-700 mt-2 rounded-lg bg-amber-50 px-3 py-2 ring-1 ring-amber-200" data-testid="paypal-sandbox-warning">Modalità TEST: i clienti vedranno l'avviso "modalità test PayPal" e nessun pagamento sarà reale. Ricorda di tornare su Live.</p>}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {MODES.map((m) => <CredsBox key={m.id} m={m} creds={cfg.creds[m.id]} onChange={(c) => setCfg({ ...cfg, creds: { ...cfg.creds, [m.id]: c } })} secret={secrets[m.id]} setSecret={(v) => setSecrets({ ...secrets, [m.id]: v })} onTest={test} busy={busy} active={cfg.mode === m.id} />)}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={busy} data-testid="paypal-save" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">Salva</Button>
          <span className="text-xs text-zinc-500 self-center">Attiva: <b>{active.label}</b>{!cfg.creds[cfg.mode]?.has_secret && " · credenziali mancanti per questa modalità"}</span>
        </div>
      </div>

      <div className="card-elev p-5 space-y-2 text-sm" data-testid="paypal-howto">
        <div className="font-semibold">Dove trovare Client ID e Secret</div>
        <ol className="list-decimal pl-5 space-y-1 text-zinc-600">
          <li>Vai su <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">developer.paypal.com → Apps & Credentials <ExternalLink className="h-3 w-3" /></a> e accedi con l'account PayPal Business del club.</li>
          <li>In alto scegli la scheda <b>Sandbox</b> oppure <b>Live</b>, poi <b>Create App</b> (tipo "Merchant"): ogni scheda ha le sue credenziali, incollale nel riquadro corrispondente qui sopra.</li>
          <li>Per i test: in <a href="https://developer.paypal.com/dashboard/accounts" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Sandbox → Accounts <ExternalLink className="h-3 w-3" /></a> trovi (o crei) un account <b>Personal</b> di prova con email e password: usalo per pagare un contributo di gruppo o una gift card dal portale. Il saldo è finto.</li>
          <li>Con Sandbox attiva puoi provare tutto il ciclo dei regali di gruppo: contributo → chiusura/scadenza → <b>rimborso</b> dalla pagina Gift Card. I movimenti si vedono su <a href="https://www.sandbox.paypal.com" target="_blank" rel="noreferrer" className="underline">sandbox.paypal.com</a> con l'account di prova.</li>
        </ol>
        <p className="text-xs text-zinc-500">Il pagamento del <b>canone piattaforma</b> con PayPal usa invece le credenziali impostate dal gestore della piattaforma (variabili PAYPAL_CLIENT_ID / PAYPAL_CLIENT_SECRET / PAYPAL_MODE).</p>
      </div>
    </div>
  );
}
